/**
 * livechat-store.ts
 *
 * Persistence and in-memory store layer for the standalone Live Chat widget.
 *
 * Responsibilities:
 *   - Types: ChatRole, ChatMessage, Conversation, ConversationSummary,
 *     LiveChatStatus, LiveChatUser
 *   - localStorage load / save / init (keyed per signed-in user)
 *   - Conversation factory helpers (makeConversation, deriveTitle, uid)
 *
 * No React, no socket.io — safe to unit-test in isolation. Every conversation
 * is human-support (VChat) only; there is no bot mode.
 */

export type ChatRole = 'user' | 'agent' | 'system';

/** Attachment kind for non-text messages (undefined = plain text). */
export type ChatKind = 'image' | 'file';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  time: number;
  /** Display name for agent (human) messages. */
  agentName?: string;
  /** Set for image/file messages; drives attachment rendering. */
  kind?: ChatKind;
  /** Public URL of the uploaded attachment. */
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
}

export interface Conversation {
  /** Local id — also the request-human room hint. */
  id: string;
  /** Server room id once the VChat room is open. */
  roomId: string | null;
  title: string;
  messages: ChatMessage[];
  agentName: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Lightweight item for the conversation switcher. */
export interface ConversationSummary {
  id: string;
  title: string;
  connected: boolean;
  updatedAt: number;
}

/** idle = no room yet · connecting = reaching an agent · live = room open. */
export type LiveChatStatus = 'idle' | 'connecting' | 'live';

export interface LiveChatUser {
  /** Authenticated user id (Keycloak sub / uuid) — used as the chat session id. */
  id?: string;
  name?: string;
  email?: string;
}

export interface StoreShape {
  conversations: Conversation[];
  activeId: string;
}

// ── Internal constants ──────────────────────────────────────────────────────
const STORE_VERSION = 'v1';
const DEFAULT_TITLE = 'Support chat';

// The VChat API requires a proper UUID for the session/room id.
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ── Utilities ───────────────────────────────────────────────────────────────
export function uid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(16)}`;
}

/** Derive a short conversation title from the first user message. */
export function deriveTitle(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (!t) return DEFAULT_TITLE;
  return t.length > 40 ? `${t.slice(0, 40)}…` : t;
}

export function makeConversation(): Conversation {
  const now = Date.now();
  return {
    id: uid(),
    roomId: null,
    title: DEFAULT_TITLE,
    messages: [],
    agentName: null,
    createdAt: now,
    updatedAt: now,
  };
}

/** Stable localStorage key scoped to the signed-in user. */
export function storageKey(user: LiveChatUser | null): string {
  return `livechat/${STORE_VERSION}/${user?.id?.trim() || 'anon'}`;
}

/** Tie the chat session to the signed-in user (stable across reloads); fall back random. */
export function identitySession(user: LiveChatUser | null): string {
  const id = user?.id?.trim();
  return id && UUID_RE.test(id) ? id : uid();
}

/** Best-effort display name for the human-support handshake. */
export function resolveName(user: LiveChatUser | null): string {
  return user?.name?.trim() || user?.email?.trim().split('@')[0] || 'Partner user';
}

// ── localStorage persistence ────────────────────────────────────────────────
export function loadStore(user: LiveChatUser | null): StoreShape | null {
  try {
    const raw = localStorage.getItem(storageKey(user));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoreShape>;
    if (!parsed?.conversations?.length) return null;
    return {
      conversations: parsed.conversations,
      activeId: parsed.activeId ?? parsed.conversations[0].id,
    };
  } catch {
    return null;
  }
}

export function saveStore(user: LiveChatUser | null, store: StoreShape): void {
  try {
    localStorage.setItem(storageKey(user), JSON.stringify(store));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function initStore(user: LiveChatUser | null): StoreShape {
  const loaded = loadStore(user);
  if (loaded) return loaded;
  const c = makeConversation();
  return { conversations: [c], activeId: c.id };
}

/** Re-exported constant so callers don't need to import from the private constant. */
export { DEFAULT_TITLE };
